/**
 * SumUpTerminalPanel — Live-Anbindung SumUp Solo (Cloud API), Jul 2026.
 * Terminal per Pairing-Code koppeln + Zahlung aus der App am Terminal auslösen.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CreditCard, Link2, Trash2, Send, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function SumUpTerminalPanel() {
    const [readers, setReaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pairCode, setPairCode] = useState("");
    const [pairName, setPairName] = useState("");
    const [pairing, setPairing] = useState(false);
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [readerId, setReaderId] = useState("");
    const [sending, setSending] = useState(false);
    const [lastPayment, setLastPayment] = useState(null);

    const loadReaders = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/buchhaltung/ec/sumup/readers");
            const items = r.data.items || [];
            setReaders(items);
            if (items.length > 0) setReaderId((prev) => prev || items[0].id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "SumUp-Terminals konnten nicht geladen werden");
        }
        setLoading(false);
    }, []);

    useEffect(() => { loadReaders(); }, [loadReaders]);

    const pair = async () => {
        if (pairCode.trim().length < 6) { toast.error("Bitte den Pairing-Code vom Terminal eingeben"); return; }
        setPairing(true);
        try {
            await api.post("/buchhaltung/ec/sumup/readers/pair", { pairing_code: pairCode, name: pairName });
            toast.success("Terminal gekoppelt");
            setPairCode(""); setPairName("");
            loadReaders();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kopplung fehlgeschlagen");
        }
        setPairing(false);
    };

    const unpair = async (id) => {
        if (!window.confirm("Terminal wirklich entkoppeln?")) return;
        try {
            await api.delete(`/buchhaltung/ec/sumup/readers/${id}`);
            toast.success("Terminal entkoppelt");
            setReaderId("");
            loadReaders();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entkoppeln fehlgeschlagen");
        }
    };

    const sendPayment = async () => {
        const val = parseFloat(String(amount).replace(",", "."));
        if (!val || val <= 0) { toast.error("Bitte einen gültigen Betrag eingeben"); return; }
        setSending(true);
        try {
            const r = await api.post("/buchhaltung/ec/sumup/terminal-checkout", {
                amount: val, description, reader_id: readerId,
            });
            setLastPayment(r.data.payment);
            toast.success(`${fmtEur(val)} an das Terminal gesendet — Karte am Gerät vorhalten`);
            setAmount(""); setDescription("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zahlung konnte nicht ausgelöst werden");
        }
        setSending(false);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="sumup-terminal-panel">
            <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4" />
                <h3 className="font-bold text-base">SumUp Solo-Terminal</h3>
                <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm font-bold">Live</span>
                <button type="button" onClick={loadReaders} className="ml-auto text-gray-500 hover:text-gray-800" title="Aktualisieren" data-testid="sumup-readers-refresh">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Terminals + Kopplung */}
                <div className="border border-gray-200 rounded-sm p-3">
                    <div className="font-bold text-sm mb-2">Gekoppelte Terminals</div>
                    {loading ? (
                        <div className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Lade…</div>
                    ) : readers.length === 0 ? (
                        <div className="text-xs text-gray-500" data-testid="sumup-no-readers">Noch kein Terminal gekoppelt.</div>
                    ) : (
                        <div className="space-y-1.5" data-testid="sumup-readers-list">
                            {readers.map((r) => (
                                <div key={r.id} className="flex items-center justify-between text-sm border border-gray-100 rounded-sm px-2 py-1.5" data-testid={`sumup-reader-${r.id}`}>
                                    <div>
                                        <span className="font-bold">{r.name || r.device?.model || "Solo"}</span>
                                        <span className="text-[11px] text-gray-500 ml-2">{r.device?.identifier || r.id} · {r.status || "?"}</span>
                                    </div>
                                    <button type="button" onClick={() => unpair(r.id)} className="text-gray-400 hover:text-red-600" data-testid={`sumup-unpair-${r.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="text-[11px] text-gray-500 mb-1.5">
                            Neues Terminal koppeln: am Solo abmelden → Verbindungen → API → „Connect" → Code hier eingeben.
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <input value={pairCode} onChange={(e) => setPairCode(e.target.value)}
                                placeholder="Pairing-Code" maxLength={12}
                                className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm w-32 uppercase"
                                data-testid="sumup-pairing-code-input" />
                            <input value={pairName} onChange={(e) => setPairName(e.target.value)}
                                placeholder="Name (z. B. Theke)"
                                className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm flex-1 min-w-[120px]"
                                data-testid="sumup-pairing-name-input" />
                            <Button type="button" onClick={pair} disabled={pairing}
                                className="h-8 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-xs font-black"
                                data-testid="sumup-pair-btn">
                                {pairing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Link2 className="w-3 h-3 mr-1" />} Koppeln
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Zahlung auslösen */}
                <div className="border border-gray-200 rounded-sm p-3">
                    <div className="font-bold text-sm mb-2">Zahlung am Terminal auslösen</div>
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <input value={amount} onChange={(e) => setAmount(e.target.value)}
                                placeholder="Betrag in €" inputMode="decimal"
                                className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm w-32"
                                data-testid="sumup-amount-input" />
                            {readers.length > 1 && (
                                <select value={readerId} onChange={(e) => setReaderId(e.target.value)}
                                    className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm flex-1"
                                    data-testid="sumup-reader-select">
                                    {readers.map((r) => (
                                        <option key={r.id} value={r.id}>{r.name || r.device?.model || r.id}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <input value={description} onChange={(e) => setDescription(e.target.value)}
                            placeholder="Beschreibung (optional, z. B. Rechnungs-Nr.)"
                            className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm w-full"
                            data-testid="sumup-description-input" />
                        <Button type="button" onClick={sendPayment} disabled={sending || readers.length === 0}
                            className="w-full h-9 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] text-sm font-black"
                            data-testid="sumup-send-payment-btn">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                            An Terminal senden
                        </Button>
                        {readers.length === 0 && !loading && (
                            <div className="text-[11px] text-amber-700">Zuerst ein Terminal koppeln, dann können Zahlungen ausgelöst werden.</div>
                        )}
                        {lastPayment && (
                            <div className="text-[11px] text-emerald-700" data-testid="sumup-last-payment">
                                Gesendet: {fmtEur(lastPayment.amount)}{lastPayment.description ? ` · ${lastPayment.description}` : ""}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
